import React, { useState, useEffect, useRef } from 'react';
import { TagBadge, Tag } from './TagBadge';
import { Button } from '@/components/ui/button';
import { Filter, X, CheckIcon } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { cn } from '@/lib/utils';
import { useI18n } from '../hooks/useI18n';

export interface TagFilterSelection {
  tagIds: string[];
  matchMode: 'AND' | 'OR';
}

interface TagFilterProps {
  entityType: string;
  onFilterChange: (selection: TagFilterSelection | null) => void;
  value?: TagFilterSelection | null;
  className?: string;
}

/**
 * Tag-based filtering with AND/OR logic
 * Now passes tag selection info to parent instead of fetching entity IDs
 */
export function TagFilter({ entityType, onFilterChange, value, className }: TagFilterProps) {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [matchMode, setMatchMode] = useState<'AND' | 'OR'>('AND');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    loadTags();
  }, []);

  // Sync internal state with external value prop
  useEffect(() => {
    if (value === null) {
      setSelectedTags([]);
      setMatchMode('AND');
    }
  }, [value]);

  const hasNotifiedParent = useRef(false);

  useEffect(() => {
    // Avoid notifying parent on mount with an empty selection — that would
    // reset pagination on pages that restore state from the URL (e.g. back nav).
    if (!hasNotifiedParent.current && selectedTags.length === 0) {
      return;
    }
    hasNotifiedParent.current = true;

    if (selectedTags.length > 0) {
      onFilterChange({
        tagIds: selectedTags.map(t => t.id),
        matchMode: matchMode
      });
    } else {
      onFilterChange(null);
    }
  }, [selectedTags, matchMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const loadTags = async () => {
    try {
      const response = await apiFetch('/api/tags?include_usage=true');
      if (response.ok) {
        const data: Tag[] = await response.json();
        // Only show tags that are actually used
        setAllTags(data.filter((tag) => (tag.usage_count || 0) > 0));
      }
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleToggleTag = (tag: Tag) => {
    const isSelected = selectedTags.some((t) => t.id === tag.id);
    if (isSelected) {
      setSelectedTags(selectedTags.filter((t) => t.id !== tag.id));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const clearFilters = () => {
    setSelectedTags([]);
  };

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      {/* Selected tags displayed inline */}
      {selectedTags.map((tag) => (
        <div key={tag.id} className="flex items-center gap-1">
          <TagBadge tag={tag} size="xs" />
          <button
            onClick={() => handleToggleTag(tag)}
            className="text-gray-500 hover:text-gray-700"
            type="button"
            title={t('tags.removeTag', 'common')}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
{/* Match mode selector - shown when multiple tags selected */}
      {selectedTags.length > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <div className="inline-flex rounded-md border border-input bg-background p-0.5">
            <button
              onClick={() => setMatchMode('AND')}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded transition-all',
                matchMode === 'AND'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              type="button"
            >
              {t('tags.matchAll', 'common')}
            </button>
            <button
              onClick={() => setMatchMode('OR')}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded transition-all',
                matchMode === 'OR'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              type="button"
            >
              {t('tags.matchAny', 'common')}
            </button>
          </div>
        </div>
      )}


      {/* Add tag button */}
      <div className="relative" ref={dropdownRef}>
        <Button
          onClick={() => setIsOpen(!isOpen)}
          variant="outline"
          className="gap-2 h-9"
          type="button"
        >
          <Filter className="h-4 w-4" />
          {selectedTags.length === 0 ? t('tags.addTagFilter', 'common') : t('tags.addTag', 'common')}
        </Button>

        {/* Tag picker dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-64 mt-1 bg-popover text-popover-foreground border rounded-md shadow-md max-h-60 overflow-y-auto right-0 p-1">
            {allTags.map((tag) => {
              const isSelected = selectedTags.some((t) => t.id === tag.id);
              return (
                <button
                  key={tag.id}
                  onClick={() => handleToggleTag(tag)}
                  className={cn(
                    'relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none',
                    'focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                  type="button"
                >
                  <TagBadge tag={tag} size="xs" />
                  {isSelected && (
                    <span className="absolute right-2 flex size-3.5 items-center justify-center">
                      <CheckIcon className="size-4" />
                    </span>
                  )}
                </button>
              );
            })}

            {allTags.length === 0 && (
              <div className="px-2 py-1.5 text-muted-foreground text-xs">
                {t('tags.noTagsInUse', 'common')}
              </div>
            )}
          </div>
        )}
      </div>


    </div>
  );
}
